import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';

import {
  playmakerMetadata as PlaymakerMetadataPropType,
  playmakerTeaseStyle as PlaymakerTeaseStylePropType,
} from 'lib/CustomPropTypes';
import { isImage } from 'lib/getAimsUrl';
import {
  getPlaymakerById,
  isAutoplay,
  NEWS_NOW_DEFAULT_SLATE,
  TODAY_ALL_DAY_DEFAULT_SLATE,
} from 'lib/playmaker';

import Dek from 'components/Dek';
import Link from 'components/Link';
import PlaymakerPlayer from 'components/PlaymakerPlayer';
import VideoSlate from 'components/VideoPlayer/VideoSlate';

function PlaymakerTeaseDefault(props) {
  const [isLive, setIsLive] = React.useState(false);

  const [hasClickedToPlay, setHasClickedToPlay] = React.useState(false);

  const {
    block,
    playmakerMetadata,
    hasClickedToPlay: hasClickedToPlayProps,
    handleStatus,
    onPlayClick,
    showDek,
    showTitle,
    stickyEnabled,
    styleOverride,
    vertical,
  } = props;

  const {
    playmakerArt,
    playmakerAutoplayMuted,
    playmakerDescription,
    playmakerId,
    playmakerPlayBehavior,
    playmakerTitle,
    playmakerFlag,
  } = playmakerMetadata;

  const getStatus = () => {
    getPlaymakerById(playmakerId)
      .then((playmaker) => {
        const {
          status,
          isLive: videoIsLive,
        } = playmaker;
        // Update our state
        setIsLive(videoIsLive);
        // Update parent status state
        handleStatus(status, videoIsLive);
      });
  };

  const handlePlayClick = () => {
    // Trigger playClick prop function
    onPlayClick();
    // Update state,
    setHasClickedToPlay(true);
  };

  const handleVideoComplete = () => {
    getStatus();
  };

  // On mount
  React.useEffect(() => {
    getStatus();
  }, []);

  const isVideoPlaying = hasClickedToPlay || hasClickedToPlayProps || isAutoplay(playmakerMetadata);

  const hasPlaymakerId = !!playmakerId;
  const isAutoplayMuted = playmakerAutoplayMuted !== false; // default play muted
  const defaultSlateImage = vertical === 'today' ? TODAY_ALL_DAY_DEFAULT_SLATE : NEWS_NOW_DEFAULT_SLATE;

  // If user defined the playmaker art as a static image, use that instead of preview
  const isArtImageUrl = playmakerArt && isImage(playmakerArt);

  return (
    <section className={block}>
      <div className={styleOverride.gridOuter}>
        <div className={styleOverride.gridCol1}>
          <div className={`${block}__video-outer`}>
            <div className={`${block}__video-inner`}>
              {hasPlaymakerId && isVideoPlaying && (
                <PlaymakerPlayer
                  autoplay={playmakerPlayBehavior === 'autoplay'}
                  autoplayMuted={isAutoplayMuted}
                  isLive={isLive}
                  onVideoComplete={handleVideoComplete}
                  pid={parseInt(playmakerId, 10) || playmakerId}
                  stickyEnabled={stickyEnabled}
                />
              )}
              {hasPlaymakerId && !isVideoPlaying && (
                <VideoSlate
                  {...props}
                  isLive={isLive}
                  pageView="front"
                  video={{
                    primaryImage: {
                      url: {
                        primary: isArtImageUrl ? playmakerArt : defaultSlateImage,
                      },
                    },
                    playable: true,
                  }}
                  onPlayClick={handlePlayClick}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styleOverride.gridCol2}>
          <h3
            className={classNames(
              `${block}__headline`,
              styleOverride.headline,
            )}
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              className={`${block}__headline-link`}
              onClick={handlePlayClick}
              to={undefined}
            >
              {/* TODO: BENTO-15783 extract this flag to sub component */}
              {(playmakerFlag || isLive) && (
                <span
                  className={classNames(
                    `${block}__flag`,
                    { [`${block}__flag--live`]: isLive },
                  )}
                >
                  {isLive && (
                    <span className={`${block}__dot`} />
                  )}
                  {playmakerFlag || i18next.t('Live')}
                  &nbsp;
                  {'/ '}
                </span>
              )}
              {showTitle && playmakerTitle}
            </Link>
          </h3>
          {showDek && (
            <Dek
              dek={playmakerDescription}
              className={`${block}__dek`}
            />
          )}
        </div>
      </div>
    </section>
  );
}

PlaymakerTeaseDefault.propTypes = {
  block: PropTypes.string.isRequired,
  handleStatus: PropTypes.func,
  hasClickedToPlay: PropTypes.bool,
  onPlayClick: PropTypes.func,
  playmakerMetadata: PlaymakerMetadataPropType.isRequired,
  showDek: PropTypes.bool,
  showTitle: PropTypes.bool,
  stickyEnabled: PropTypes.bool,
  styleOverride: PlaymakerTeaseStylePropType.isRequired,
  useDefault: PropTypes.bool,
  vertical: PropTypes.string.isRequired,
};

PlaymakerTeaseDefault.defaultProps = {
  handleStatus: Function.prototype,
  hasClickedToPlay: false,
  onPlayClick: Function.prototype,
  showDek: true,
  showTitle: true,
  stickyEnabled: false,
  useDefault: false,
};

export { PlaymakerTeaseDefault };
